import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { CheckCircle, Lightning, Crown, CreditCard, Receipt, XCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useCompany } from '../context/CompanyContext';
import { getBillingUsage } from '../lib/api';
import QuoteRequestDialog from '../components/QuoteRequestDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  starter: 'border-border bg-card',
  grow: 'border-slate-400 bg-slate-50',
  core: 'border-brand bg-brand/5',
  scale: 'border-amber-500 bg-amber-50',
  business: 'border-emerald-600 bg-emerald-50',
  enterprise: 'border-emerald-700 bg-emerald-50',
  pending_payment: 'border-rose-500 bg-rose-50',
};

export default function Billing() {
  const { company, loading, refresh } = useCompany();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [cycle, setCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [polling, setPolling] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quoteFeatures, setQuoteFeatures] = useState([]);
  const [usage, setUsage] = useState(null);
  const [annualUpsell, setAnnualUpsell] = useState(null); // { plan_key, monthly_price } | null
  const location = useLocation();
  const navigate = useNavigate();
  const pollingRef = useRef(false);

  const loadTransactions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/billing/transactions`, { withCredentials: true });
      setTransactions(data || []);
    } catch {
      setTransactions([]);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/plans`).then((r) => setPlans(r.data)).catch(() => setPlans([]));
    loadTransactions();
    getBillingUsage().then((r) => setUsage(r.data)).catch(() => setUsage(null));
  }, [loadTransactions]);

  // Handle return from Stripe checkout (success_url: /billing?session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get('session_id');
    const cancelled = params.get('cancelled');

    if (cancelled) {
      toast.error('Checkout cancelled — no charge made');
      navigate('/billing', { replace: true });
      return;
    }
    if (!sid || pollingRef.current) return;
    pollingRef.current = true;
    setPolling(true);

    const poll = async (attempts = 0) => {
      const max = 10;
      if (attempts >= max) {
        setPolling(false);
        toast.error('Still waiting on payment confirmation. Check transactions below in a minute.');
        navigate('/billing', { replace: true });
        return;
      }
      try {
        const { data } = await axios.get(`${API}/api/billing/checkout/status/${sid}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          toast.success(`Paid — welcome to ${data.plan_key.toUpperCase()}!`);
          await refresh();
          await loadTransactions();
          setPolling(false);
          // Inline upsell: monthly buyers get an annual upgrade nudge
          if (data.billing_cycle === 'monthly' && data.plan_key !== 'starter') {
            setAnnualUpsell({ plan_key: data.plan_key });
          }
          navigate('/billing', { replace: true });
          return;
        }
        if (data.status === 'expired') {
          toast.error('Checkout session expired');
          setPolling(false);
          navigate('/billing', { replace: true });
          return;
        }
        setTimeout(() => poll(attempts + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(attempts + 1), 2000);
      }
    };
    poll();
  }, [location.search, navigate, refresh, loadTransactions]);

  // Check the URL on mount — if we landed here from /pricing?cycle=annual, pre-select that.
  useEffect(() => {
    const initialUpgrade = new URLSearchParams(location.search).get('upgrade');
    const initialCycle = new URLSearchParams(location.search).get('cycle');
    if (initialCycle === 'annual' || initialCycle === 'monthly') setCycle(initialCycle);
    if (initialUpgrade) {
      // auto-trigger checkout if we came in with ?upgrade=core
      // (deferred so company/plans state has time to settle)
      setTimeout(() => upgrade(initialUpgrade, initialCycle || 'monthly'), 250);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upgrade = async (planKey, overrideCycle) => {
    const billingCycle = overrideCycle || cycle;
    setBusy(planKey);
    try {
      const { data } = await axios.post(
        `${API}/api/billing/checkout`,
        { plan_key: planKey, origin_url: window.location.origin, cycle: billingCycle },
        { withCredentials: true }
      );
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
      setBusy(null);
    }
  };

  if (loading) return <Layout><div className="p-6 text-muted-foreground">Loading…</div></Layout>;
  if (!company) return <Layout><div className="p-6 text-muted-foreground">No company loaded.</div></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="billing-page">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            <Crown size={24} weight="fill" className="inline text-brand mr-2" /> Plan & Billing
          </h1>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">Manage subscription, usage, and invoices</p>
        </div>

        {polling && (
          <div className="border border-amber-500 bg-amber-50 p-3 text-xs uppercase font-bold tracking-widest flex items-center gap-2" data-testid="billing-polling">
            <div className="w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
            Confirming payment…
          </div>
        )}

        {annualUpsell && (() => {
          const plan = plans.find((p) => p.key === annualUpsell.plan_key);
          const monthly = plan?.price_monthly_usd || 0;
          const annual = Math.round(monthly * 10);
          const savings = Math.round(monthly * 2);
          return (
            <div
              data-testid="billing-annual-upsell-card"
              className="border-2 border-emerald-600 bg-emerald-50 p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-600 flex items-center justify-center shrink-0">
                  <Crown size={20} weight="fill" className="text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-emerald-800 mb-1">
                    Annual offer · save 17%
                  </div>
                  <h3 className="text-lg font-bold text-foreground">
                    Switch to annual & save A${savings.toLocaleString()}/yr
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    You're on <span className="font-mono uppercase">{annualUpsell.plan_key}</span> monthly · pay 10 months,
                    get 12. Total <span className="font-mono">A${annual.toLocaleString()}</span>/year.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button
                  data-testid="billing-annual-upsell-accept"
                  onClick={() => {
                    setAnnualUpsell(null);
                    upgrade(annualUpsell.plan_key, 'annual');
                  }}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold uppercase tracking-widest text-xs h-10 px-4"
                >
                  <Lightning size={14} weight="fill" className="mr-2" />
                  Switch to annual
                </Button>
                <button
                  onClick={() => setAnnualUpsell(null)}
                  data-testid="billing-annual-upsell-dismiss"
                  className="text-[11px] text-muted-foreground hover:text-foreground font-mono uppercase tracking-widest px-2"
                >
                  Dismiss
                </button>
              </div>
            </div>
          );
        })()}

        {/* Active promo discount banner */}
        {company.pending_discount && company.pending_discount.code && (
          <div className="border-2 border-amber-500 bg-amber-50 p-4 flex items-center gap-4" data-testid="billing-discount-banner">
            <div className="text-3xl">🎟️</div>
            <div className="flex-1">
              <div className="font-bold text-amber-900">
                {company.pending_discount.percent_off}% off your next {(company.pending_discount.cycles || 0) - (company.pending_discount.applied_to_invoices || 0)} payment{((company.pending_discount.cycles || 0) - (company.pending_discount.applied_to_invoices || 0)) !== 1 ? 's' : ''}
              </div>
              <div className="text-xs text-amber-700 mt-0.5">
                Code <code className="font-mono font-bold">{company.pending_discount.code}</code> · {company.pending_discount.applied_to_invoices || 0} of {company.pending_discount.cycles} cycle{company.pending_discount.cycles !== 1 ? 's' : ''} consumed.
              </div>
            </div>
          </div>
        )}
        {company.free_forever_grant && (
          <div className="border-2 border-amber-500 bg-amber-50 p-4 flex items-center gap-4" data-testid="billing-vip-banner">
            <div className="text-3xl">👑</div>
            <div className="flex-1">
              <div className="font-bold text-amber-900">VIP — Free forever access granted</div>
              <div className="text-xs text-amber-700 mt-0.5">
                Code <code className="font-mono font-bold">{company.free_forever_grant.code}</code> unlocked the {company.free_forever_grant.plan_key?.toUpperCase()} plan permanently. No Stripe subscription needed.
              </div>
            </div>
          </div>
        )}

        {/* Current plan summary */}
        <div className={`border p-6 ${TIER_COLORS[company.plan_key] || 'border-border'}`} data-testid="current-plan-card">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Current plan</div>
              <div className="text-2xl font-bold">{company.plan_name}</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono uppercase">
                {company.status === 'pending_payment'
                  ? 'Pending payment · add a card to activate'
                  : `Status: ${company.status}`}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Usage this month</div>
              <div className="grid grid-cols-3 gap-4 mt-2 text-center">
                <UsageTile label="Users" used={company.usage?.users || 0} limit={company.limits?.users} />
                <UsageTile label="SKUs" used={company.usage?.skus || 0} limit={company.limits?.skus} />
                <UsageTile label="Orders/30d" used={company.usage?.orders_last_30d || 0} limit={company.limits?.orders_per_month} />
              </div>
            </div>
          </div>
        </div>

        {/* Usage meters — current period vs effective limits */}
        {usage && (
          <div className="border border-border bg-card p-5" data-testid="billing-usage-meters">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Receipt size={18} weight="fill" className="text-brand" /> Usage this month
              </h2>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                Period: {usage.period}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {usage.meters.map((m) => (
                <UsageMeter key={m.metric} meter={m} />
              ))}
            </div>
          </div>
        )}

        {/* Plan picker */}
        <div>
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-lg font-bold">Available plans</h2>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center bg-muted border border-border p-1" data-testid="billing-cycle-toggle">
                <button
                  onClick={() => setCycle('monthly')}
                  data-testid="billing-cycle-monthly"
                  className={`px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${cycle === 'monthly' ? 'bg-brand text-white' : 'text-muted-foreground'}`}
                >Monthly</button>
                <button
                  onClick={() => setCycle('annual')}
                  data-testid="billing-cycle-annual"
                  className={`px-3 py-1 text-[11px] font-bold uppercase tracking-widest ${cycle === 'annual' ? 'bg-brand text-white' : 'text-muted-foreground'}`}
                >
                  Annual <span className="ml-1 text-emerald-600">save 17%</span>
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/settings/billing/addons')}
                className="rounded-none"
                data-testid="billing-addons-link"
              >
                <Lightning size={14} weight="fill" className="mr-1" /> Browse add-ons
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {plans.filter((p) => p.key !== 'trial').map((p) => {
              const isCurrent = company.plan_key === p.key && company.status !== 'trial';
              const monthly = p.price_monthly_usd;
              const isCustom = monthly == null;
              const displayPrice = isCustom ? null : (cycle === 'annual' ? Math.round(monthly * 10) : monthly);
              const cycleLabel = cycle === 'annual' ? '/yr' : '/mo';
              return (
                <div
                  key={p.key}
                  data-testid={`plan-card-${p.key}`}
                  className={`border p-6 flex flex-col ${isCurrent ? 'border-brand bg-brand/5' : 'border-border'}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">{p.name}</div>
                  <div className="text-3xl font-bold mt-1 tabular-nums">
                    {isCustom ? (
                      <>Custom<span className="text-xs text-muted-foreground font-mono ml-2">pricing</span></>
                    ) : (
                      <>A${displayPrice.toLocaleString()}<span className="text-xs text-muted-foreground font-mono ml-1">{cycleLabel}</span><div className="text-[9px] text-muted-foreground font-mono uppercase tracking-widest mt-0.5">GST inc.</div></>
                    )}
                  </div>
                  {!isCustom && cycle === 'annual' && (
                    <div className="text-[10px] text-emerald-700 font-mono uppercase tracking-widest mt-0.5" data-testid={`plan-annual-savings-${p.key}`}>
                      Save A${Math.round(monthly * 2)}/yr
                    </div>
                  )}
                  <ul className="mt-4 space-y-1.5 text-xs flex-1">
                    {(p.features || []).slice(0, 10).map((f) => (
                      <li key={f} className="flex items-center gap-2 text-foreground">
                        <CheckCircle size={12} weight="fill" className="text-emerald-600 shrink-0" />
                        <span className="font-mono uppercase">{f.replace(/_/g, ' ')}</span>
                      </li>
                    ))}
                    {(p.features || []).length > 10 && (
                      <li className="text-[10px] text-muted-foreground font-mono">+ {p.features.length - 10} more</li>
                    )}
                  </ul>
                  {isCurrent ? (
                    <div className="mt-4 text-center text-xs uppercase tracking-widest font-bold text-brand" data-testid={`plan-current-${p.key}`}>
                      <CheckCircle size={14} weight="fill" className="inline mr-1" />
                      Current plan{company.billing_cycle ? ` · ${company.billing_cycle}` : ''}
                    </div>
                  ) : isCustom ? (
                    <Button
                      data-testid={`request-quote-${p.key}`}
                      onClick={() => { setQuoteFeatures(p.features || []); setQuoteOpen(true); }}
                      className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-none font-bold"
                    >
                      <PaperPlaneTilt size={14} weight="bold" className="mr-2" /> Request a quote
                    </Button>
                  ) : (
                    <Button
                      data-testid={`select-plan-${p.key}`}
                      onClick={() => upgrade(p.key)}
                      disabled={busy !== null}
                      className="w-full mt-4 bg-brand hover:bg-brand/90 text-white rounded-none font-bold"
                    >
                      {busy === p.key ? 'Redirecting…' : (
                        <><CreditCard size={14} weight="bold" className="mr-2" />Upgrade to {p.name}</>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Transactions */}
        {transactions.length > 0 && (
          <div className="border border-border p-6" data-testid="billing-transactions">
            <h2 className="text-lg font-bold mb-3">
              <Receipt size={18} weight="fill" className="inline mr-2 text-muted-foreground" /> Billing history
            </h2>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Plan</th>
                  <th className="text-right py-2">Amount (inc GST)</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Tax invoice</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx) => (
                  <tr key={tx.session_id} className="border-b border-border hover:bg-muted" data-testid={`tx-${tx.session_id}`}>
                    <td className="py-2 font-mono text-xs text-muted-foreground">{(tx.paid_at || tx.created_at || '').slice(0, 16)}</td>
                    <td className="py-2 font-mono uppercase">{tx.plan_key || (tx.kind === 'addon' ? `addon:${tx.addon_key}` : '—')}</td>
                    <td className="py-2 text-right font-mono">A${(tx.total_inc_gst ?? tx.amount_usd ?? 0).toFixed(2)}</td>
                    <td className="py-2">
                      {tx.status === 'completed' ? (
                        <span className="text-xs font-bold text-emerald-600 uppercase font-mono"><CheckCircle size={12} weight="fill" className="inline mr-1" />Paid</span>
                      ) : tx.status === 'expired' || tx.payment_status === 'failed' ? (
                        <span className="text-xs font-bold text-rose-600 uppercase font-mono"><XCircle size={12} weight="fill" className="inline mr-1" />Failed</span>
                      ) : (
                        <span className="text-xs font-bold text-amber-600 uppercase font-mono">{tx.status}</span>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {tx.status === 'completed' ? (
                        <a
                          href={`${API}/api/billing/tax-invoice/${tx.session_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`tax-invoice-${tx.session_id}`}
                          className="text-xs font-bold text-brand hover:underline font-mono uppercase tracking-widest"
                        >
                          <Receipt size={12} weight="fill" className="inline mr-1" />Download
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground font-mono">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center mt-8 font-mono">
          Secured by Stripe · Test mode ·{' '}
          <button onClick={refresh} className="text-brand underline">Refresh plan</button>
        </p>
      </div>

      <QuoteRequestDialog
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
        prefillFeatures={quoteFeatures}
      />
    </Layout>
  );
}

const UsageTile = ({ label, used, limit }) => {
  const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
  return (
    <div className="min-w-[80px]">
      <div className="text-xl font-bold font-mono">{used}<span className="text-muted-foreground text-xs">/{limit ?? '∞'}</span></div>
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{label}</div>
      {limit && (
        <div className="mt-1 h-1 bg-slate-200">
          <div
            className={`h-full ${pct >= 90 ? 'bg-rose-600' : pct >= 70 ? 'bg-amber-500' : 'bg-brand'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
};


const UsageMeter = ({ meter }) => {
  const unlimited = meter.limit == null;
  const pct = unlimited ? 0 : meter.percent || 0;
  const barColor = pct >= 90 ? 'bg-rose-600' : pct >= 70 ? 'bg-amber-500' : 'bg-brand';
  return (
    <div className="border border-border bg-background p-4" data-testid={`usage-meter-${meter.metric}`}>
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{meter.label}</span>
        {!unlimited && pct >= 90 && (
          <span className="text-[9px] uppercase tracking-widest font-bold text-rose-600 bg-rose-100 px-1.5 py-0.5">
            {pct >= 100 ? 'At limit' : 'Almost full'}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold tabular-nums font-mono">
        {meter.used.toLocaleString()}
        <span className="text-base text-muted-foreground ml-1">
          / {unlimited ? '∞' : meter.limit.toLocaleString()}
        </span>
      </div>
      {!unlimited ? (
        <div className="mt-2 h-1.5 bg-slate-200">
          <div className={`h-full ${barColor} transition-all duration-300`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      ) : (
        <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-emerald-700">Unlimited</div>
      )}
    </div>
  );
};

